/**
 * The capability catalogue: the fixed set of platform abilities a persona definition can draw on. A persona names the
 * capabilities it has; the runtime gives it only the tools behind them, and nothing else. A capability that writes
 * says whether its writes are record-level (a change a user makes from the screens, approval-free like the Operations
 * Manager's) or reach outside the records (a message sent), which only an approved plan step that set changes_data may do.
 */

export type CapabilityKind = 'read' | 'write';

/** The projects app's types, which `review_projects` reads: the template's keys, which a Developer-built variant keeps. */
export const PROJECTS_APP_TYPES = { project: 'app:projects:Project', milestone: 'app:projects:Milestone', task: 'Task' } as const;

/** `none`: record-level, runs without approval. `admin`: only in a plan step an admin approved with changes_data. */
export type CapabilityApproval = 'none' | 'admin';

export interface PersonaCapabilityDefinition {
  label: string;
  /** What the capability lets the persona do, for the approval card and the roster. */
  description: string;
  kind: CapabilityKind;
  /** Only for a capability that writes. */
  approval?: CapabilityApproval;
  /**
   * For Lantern staff: it reads Lantern's own systems, so a definition may name it only in a workspace a super admin
   * linked to a Lantern workspace, and its tools are built only for a Diggens super admin.
   */
  staff?: true;
}

export const PERSONA_CAPABILITIES = {
  read_records: {
    label: 'Read records',
    description: 'Find and read records of the types in its read scope, their relationships, and the schema of those types.',
    kind: 'read',
  },
  write_records: {
    label: 'Write records',
    description: 'Create, update, link and delete records of the types in its write scope, the way a user does from the screens.',
    kind: 'write',
    approval: 'none',
  },
  lists: {
    label: 'Campaign lists',
    description: 'Find, create and change campaign lists and their members.',
    kind: 'write',
    approval: 'none',
  },
  picklists: {
    label: 'Picklists',
    description: 'Read the picklists records use, with their items.',
    kind: 'read',
  },
  dashboards: {
    label: 'Dashboards',
    description: 'Ask the Analytics agent for metric trends, comparisons and charts, and have it save dashboards.',
    kind: 'write',
    approval: 'none',
  },
  review_projects: {
    label: 'Review projects',
    description:
      'Read the state of the projects app’s projects: per active project, its milestones with the tasks overdue and due this week under each, milestones at risk, blocked tasks and blockers that recur. Needs the projects app installed and its Project type in the read scope.',
    kind: 'read',
  },
  send_slack_dm: {
    label: 'Send Slack DMs',
    description: 'Send a direct message in Slack to a user of this workspace. Reaches outside the records, so only in a plan step an admin approved.',
    kind: 'write',
    approval: 'admin',
  },
  draft_email: {
    label: 'Draft email',
    description: 'Draft an email (to, subject, body) for a person to send themselves. Nothing is sent.',
    kind: 'read',
  },
  run_on_schedule: {
    label: 'Run on a schedule',
    description: 'Carry out its standing brief on its schedule, as a run its Chief of Staff starts.',
    kind: 'read',
  },
  watch_sentry: {
    label: 'Watch Sentry',
    description: "Read Lantern's unresolved Sentry issues seen since a moment: the exception type, where it was raised, counts and links, never an event's message or data.",
    kind: 'read',
    staff: true,
  },
  watch_linear: {
    label: 'Watch Linear',
    description: "Read Lantern's open Linear issues updated since a moment: what each says, whether a person or an integration filed it, its labels and the Sentry issues it links.",
    kind: 'read',
    staff: true,
  },
} as const satisfies Record<string, PersonaCapabilityDefinition>;

export type PersonaCapability = keyof typeof PERSONA_CAPABILITIES;

export const PERSONA_CAPABILITY_KEYS = Object.keys(PERSONA_CAPABILITIES) as [PersonaCapability, ...PersonaCapability[]];

export function capabilityDefinition(capability: PersonaCapability): PersonaCapabilityDefinition {
  return PERSONA_CAPABILITIES[capability];
}

/** The catalogue as lines an agent or an admin can read. */
export function describeCapabilityCatalogue(): string {
  return PERSONA_CAPABILITY_KEYS.map((key) => {
    const { label, description, kind, approval, staff } = PERSONA_CAPABILITIES[key] as PersonaCapabilityDefinition;
    const mode = kind === 'read' ? 'reads' : approval === 'admin' ? 'writes outside the records; admin-approved changes_data steps only' : 'writes records; no approval';
    return `- ${key} (${label}; ${mode}${staff ? '; Lantern staff only, in a workspace linked to Lantern' : ''}): ${description}`;
  }).join('\n');
}
